import React, { Fragment } from 'react';
import debounce from 'lodash/debounce';
import styled from '@emotion/styled';
import { Field, FormSection, RequiredAsterisk, FormHeader, ValidMessage } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { Currency } from '@atlassian/cssxp-typescommon';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { Box, xcss, Flex, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { PaymentMethodId, PaymentMethodPanel } from '@atlassian/commerce-payment-methods';
import { SummaryPanelContainer, SummaryPanelRow, formatAddressLabel, AchPanel } from '@css-xp/base';
import { TermPaymentMethod } from '@css-xp/base/term-payment-method';
import { NoData } from '@css-xp/ppc-common';
import {
  CreditCard,
  PaymentMethod,
  PaymentMethodType,
  PaymentOnTerms,
  PaymentMethodTypeV2,
  BillingAdmin,
  Ach,
} from '@css-xp/graphtypes/src/generated-types-new';
import { PaypalPaymentMethod } from '@css-xp/ppc-paypal';
import { isAchPaymentMethod } from '@css-xp/ppc-utils';

export const FinalizeStepStyles = styled.div`
  max-height: 380px;
  overflow-y: auto;
  margin-right: -15px;
  padding-right: 15px;
`;

interface FinalizeStepProps {
  billingProfileData: any;
  availablePaymentMethods: Array<any>;
  primaryCustomerContactAccountEmails: string[];
  secondaryCustomerContactAccountEmails: string[];
  billingAdmins: BillingAdmin[];
  existingBPNameList: string[];
  shouldHideFormHeader?: boolean;
  shouldRenderForInternalUse?: boolean;
  currency?: string;
  // keyContact is specifically used for internal use
  keyContact?: BillingAdmin;
  // otherContacts is specifically used for internal use
  otherContacts?: BillingAdmin[];
}

const billingProfileNameStyles = xcss({
  paddingBottom: 'space.400',
});
const billingProfileDetailStyles = xcss({
  //@ts-ignore
  padding: `${token('space.200')} ${token('space.100')}`,
  border: `1px solid ${token('color.border')}`,
  borderRadius: '3px',
});

const getPaymentMethodSection = (defaultPaymentMethod?: PaymentMethod | null) => {
  if (!defaultPaymentMethod) {
    return <Text>No payment method selected</Text>;
  }

  if (defaultPaymentMethod?.type === PaymentMethodType?.CARD) {
    const defaultPaymentMethodDetails = defaultPaymentMethod as CreditCard;
    const expMonth = +(defaultPaymentMethodDetails?.card?.expirationMonth ?? 0);
    const expYear = +(defaultPaymentMethodDetails?.card?.expirationYear ?? 0);

    return (
      <PaymentMethodPanel
        paymentMethod={{
          id: defaultPaymentMethodDetails?.id as PaymentMethodId,
          type: defaultPaymentMethodDetails?.type,
          // Because the 'card' prop should be used with PaymentMethodType.CARD type
          ...(defaultPaymentMethodDetails?.type === PaymentMethodType.CARD && {
            card: {
              brand: defaultPaymentMethodDetails?.card?.brand ?? '',
              expMonth,
              expYear,
              last4: defaultPaymentMethodDetails?.card?.last4Numbers ?? '',
              name: defaultPaymentMethodDetails?.card?.cardHolderName ?? '',
            },
          }),
          default: defaultPaymentMethodDetails?.default ?? false,
        }}
      />
    );
  }

  if (defaultPaymentMethod?.type === PaymentMethodType?.DEFERRED) {
    const defaultPaymentMethodDetails = defaultPaymentMethod as PaymentOnTerms;
    const hasPayOnStatement = defaultPaymentMethodDetails?.terms?.payOnStatement;
    const invoiceDueDays = defaultPaymentMethodDetails?.terms?.invoiceDueDays;
    const termPaymentMethod = defaultPaymentMethodDetails?.paymentMethod;
    const termPaymentType = invoiceDueDays ? `NET${invoiceDueDays}` : termPaymentMethod;
    const totalCreditLimit = defaultPaymentMethodDetails?.totalCreditLimit;

    return (
      <TermPaymentMethod
        paymentNetType={hasPayOnStatement ? '' : (termPaymentType as string)}
        totalLimit={hasPayOnStatement ? undefined : (totalCreditLimit as number)}
        hasPayOnAccount={!!hasPayOnStatement}
        version={2}
        shouldHideVerticalDivider={Boolean(hasPayOnStatement)}
      />
    );
  }

  //@ts-ignore
  if (defaultPaymentMethod?.type === PaymentMethodTypeV2.PAYPAL) {
    const emailID = (defaultPaymentMethod as any)?.payPal?.email;
    return <PaypalPaymentMethod emailID={emailID} />;
  }

  if (isAchPaymentMethod(defaultPaymentMethod?.type)) {
    //@ts-ignore
    const achPaymentMethod = defaultPaymentMethod as Ach;
    return (
      <AchPanel
        bankName={achPaymentMethod.ach?.bankName}
        last4={achPaymentMethod.ach?.last4}
        showBorder
        showInfoInline={false}
      />
    );
  }
};

export const FinalizeStep = ({
  billingProfileData,
  availablePaymentMethods,
  primaryCustomerContactAccountEmails,
  secondaryCustomerContactAccountEmails,
  billingAdmins,
  existingBPNameList,
  shouldHideFormHeader,
  shouldRenderForInternalUse,
  currency,
  keyContact,
  otherContacts,
}: FinalizeStepProps) => {
  const selectedPaymentMethodObj = availablePaymentMethods.find(
    (paymentMethod) => paymentMethod.id === billingProfileData.defaultPaymentMethod
  );
  const [isBPNameValid, setIsBPNameNameValid] = React.useState(false);
  const [isBPNameInvalid, setIsBPNameNameInvalid] = React.useState(false);

  const billingAdminEmails = billingAdmins?.map((billingAdmin) => billingAdmin.email);

  const onBillingProfileNameChange = React.useCallback(
    debounce(async (name: string) => {
      if (!existingBPNameList?.includes(name.trim())) {
        setIsBPNameNameValid(true);
        setIsBPNameNameInvalid(false);
      } else {
        setIsBPNameNameInvalid(true);
        setIsBPNameNameValid(false);
      }
    }, 500),
    []
  );

  const renderFormHeader = () => {
    if (shouldHideFormHeader) {
      return null;
    }

    if (shouldRenderForInternalUse) {
      return (
        <FormHeader>
          <h3>3. Finalize</h3>
        </FormHeader>
      );
    }

    return (
      <FormHeader>
        <h3>Finalize</h3>
        Required fields are marked with an asterisk <RequiredAsterisk />
      </FormHeader>
    );
  };

  return (
    <FormSection>
      {renderFormHeader()}
      <FinalizeStepStyles>
        <Box id="partner-details-step" xcss={billingProfileNameStyles}>
          <Field
            name="invoiceGroupName"
            id="invoiceGroupName"
            defaultValue={billingProfileData?.invoiceGroupName}
            label="How would you like to name your billing profile?"
            isRequired={true}
          >
            {({ fieldProps, error }) => (
              <Fragment>
                <Textfield
                  {...fieldProps}
                  name="invoiceGroupName"
                  testId="invoice-group-name"
                  placeholder='e.g. "ABC New York - Finance team - Monthly”'
                  onChange={(event: React.FormEvent<HTMLInputElement>) => {
                    onBillingProfileNameChange(event.currentTarget.value);
                    fieldProps.onChange(event);
                  }}
                />
                {!error && isBPNameValid && <ValidMessage>Looking good - let's lock it in</ValidMessage>}
                {!error && isBPNameInvalid && (
                  <Box paddingBlockStart="space.050">
                    <Flex alignItems="center">
                      <WarningIcon primaryColor={token('color.icon.warning')} label="Warning" size="small" />
                      <Text size="small" color="color.text.warning">
                        Heads up - a billing profile with this name already exists
                      </Text>
                    </Flex>
                  </Box>
                )}
              </Fragment>
            )}
          </Field>
        </Box>
        <Box xcss={billingProfileDetailStyles}>
          <SummaryPanelContainer title="Billing profile details">
            {shouldRenderForInternalUse || (
              <SummaryPanelRow label="Company" value={billingProfileData?.soldToAddress?.name} />
            )}
            <SummaryPanelRow
              label="Sold-to address"
              value={
                shouldRenderForInternalUse
                  ? 'Same as billing address'
                  : formatAddressLabel(billingProfileData?.soldToAddress, true)
              }
            />
            {shouldRenderForInternalUse ? (
              <>
                <SummaryPanelRow label="Key contact" value={keyContact?.email} />
                <SummaryPanelRow
                  label="Other contact(s)"
                  value={
                    otherContacts && otherContacts?.length > 0 ? (
                      <div>
                        {otherContacts.map((item, index) => (
                          <div key={index}>{item?.email}</div>
                        ))}
                      </div>
                    ) : (
                      <NoData />
                    )
                  }
                />
              </>
            ) : (
              <>
                <SummaryPanelRow label="Primary customer contact" value={primaryCustomerContactAccountEmails[0]} />
                <SummaryPanelRow
                  label="Secondary customer contact(s)"
                  value={
                    secondaryCustomerContactAccountEmails[0] !== '' &&
                    secondaryCustomerContactAccountEmails.length > 0 ? (
                      <div>
                        {secondaryCustomerContactAccountEmails.map((email, index) => (
                          <div key={index}>{email}</div>
                        ))}
                      </div>
                    ) : (
                      <NoData />
                    )
                  }
                />
              </>
            )}
            <SummaryPanelRow
              label="Payment method"
              value={<Flex>{getPaymentMethodSection(selectedPaymentMethodObj)}</Flex>}
            />
            {shouldRenderForInternalUse || (
              <SummaryPanelRow
                label="Billing admin"
                value={
                  billingAdminEmails.length > 0 ? (
                    <div>
                      {billingAdminEmails.map((email, index) => (
                        <div key={index}>{email}</div>
                      ))}
                    </div>
                  ) : (
                    <NoData />
                  )
                }
              />
            )}
            {shouldRenderForInternalUse && <SummaryPanelRow label="Currency" value={currency ?? Currency.USD} />}
          </SummaryPanelContainer>
        </Box>
      </FinalizeStepStyles>
    </FormSection>
  );
};
